import { ReactElement } from 'react'
import { SectionTitle } from 'app/components/ai-agent-voice-ops/SectionTitle'
import { SlippingGrid } from 'app/components/ai-agent-voice-ops/SlippingGrid'
import SegmentControl from 'app/components/viz/SegmentControl'
import { DataSourceBadge } from './DataSourceBadge'
import { SILENT_THRESHOLD_LABEL } from './smsAgentOpsMetrics'
import type { SilentScope, UseSmsAgentOpsReturn } from './useSmsAgentOps'

const SCOPE_OPTIONS: { value: SilentScope; label: string }[] = [
  { value: `silent`, label: `Silent` },
  { value: `all`, label: `All SMS Agents` },
]

const AI_REPLY_LABEL = { one: `AI reply`, other: `AI replies` }

type SilentAgentsSectionProps = Pick<
  UseSmsAgentOpsReturn,
  `silentRows` | `isSilentLoading` | `silentScope` | `setSilentScope`
>

/** SMS Agent locations day by day through today: green on days the AI replied, red on days it did not (plan D17). */
export function SilentAgentsSection({
  silentRows,
  isSilentLoading,
  silentScope,
  setSilentScope,
}: SilentAgentsSectionProps): ReactElement {
  const isSilentView = silentScope === `silent`

  return (
    <div className="mb-8">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle
          title="Silent Agents"
          badge={`${silentRows?.length ?? 0} Locations`}
          adornment={<DataSourceBadge source="preview" />}
        />
        <SegmentControl
          label="Show"
          buttons={SCOPE_OPTIONS.map((option) => ({
            label: option.label,
            onClick: () => setSilentScope(option.value),
            isActive: silentScope === option.value,
          }))}
        />
      </div>
      <p className="mb-3 text-body-sm text-subtle">
        {isSilentView
          ? `SMS Agent locations with no AI reply in the last ${SILENT_THRESHOLD_LABEL}, day by day through today.`
          : `Every SMS Agent location, day by day through today.`}
      </p>
      <SlippingGrid
        rows={silentRows}
        isLoading={isSilentLoading}
        activityLabel={AI_REPLY_LABEL}
        emptyMessage={
          isSilentView
            ? `Every SMS Agent location has had an AI reply in the last ${SILENT_THRESHOLD_LABEL}.`
            : `No SMS Agent locations in scope.`
        }
      />
    </div>
  )
}
